// Copyright 2024 Sorama B.V.

import { Point2 } from "libraries/DataTypes/Point2";
import { Pressure } from "units/Pressure";
import { Unit, UnitValue } from "units/Unit";
import { UnitParser } from "units/UnitParser";
import { UnitSystem } from "units/UnitSystem";
import { ReportableMeasurement } from "./ReportableMeasurement";
import { SorXMeasurementTypeEnum } from "./SoramaWebApi";
import { SorXMetadataFull } from "./SorXFormat";

const sorXMeasurementTypes = ["image", "video", "leakDetection", "partialDischarge", "mechanicalInspection"];
export type SorXMeasurementType = (typeof sorXMeasurementTypes)[number];

const sorxUnitSystemTypes = ["metric", "imperial"];
export type SorXUnitSystemType = (typeof sorxUnitSystemTypes)[number];
function sorXUnitSystemTypeToUnitSystemType(sorXUnitSystemType: SorXUnitSystemType): UnitSystem {
    switch (sorXUnitSystemType) {
        case "metric":
            return UnitSystem.Metric;
        case "imperial":
            return UnitSystem.Imperial;
        default:
            throw new Error(`Unknown Unit System Type: ${sorXUnitSystemType}`);
    }
}

const leakTypes = ["Open Orifice", "Threaded End Cap", "Quick Connect", "Hose Slit Cut Leak", "None"];
export type LeakType = (typeof leakTypes)[number];

export const GasTypesOptions = [
    "Air",
    "Argon",
    "Helium",
    "Hydrogen",
    "Nitrogen",
    "Natural Gas",
    "Methane",
    "Oxygen",
    "CO2",
    "Ammonia",
    "Butane",
    "Propane",
    "Unknown",
    "Other",
];
export type GasType = (typeof GasTypesOptions)[number];

const leakLinePressureUnitTypes = [Pressure.pascal, Pressure.psi, Pressure.inh2o, Pressure.bar];
export type LeakLinePressureUnitType = (typeof leakLinePressureUnitTypes)[number];

const inaccurateReasonTypes = ["Sound too loud", "Sound too soft", "NA"];
export type InaccurateReasonType = (typeof inaccurateReasonTypes)[number];

const assetStatusTypes = ["undetermined", "found", "left"];
export type AssetStatusType = (typeof assetStatusTypes)[number];

export const operatingConditions = ["DC", "AC 50Hz", "AC 60Hz"];
export type OperatingConditions = (typeof operatingConditions)[number];

export type SorXMeasurement = SorXData & (SorXLDData | SorXPDDData);

export type SorXData = {
    type: SorXMeasurementType;
    notes: string;
    dateTime: Date;
    estimatedDistance: UnitValue;
    firmwareRelease: string;
    frequencySelectionFrom: UnitValue;
    frequencySelectionTo: UnitValue;
    sampleRate: UnitValue;
    splAtSource: UnitValue;
    targetPointDistance: UnitValue;
    image: string;
    featureImages: string[];

    tagAssetID: string | number;
    tagAssetName: string;
    tagAssetStatus: string;
    tagAssetType: AssetStatusType;
};

export type SorXLDData = {
    tagLeakCost: UnitValue;
    tagLeakElectricityCost: UnitValue;
    tagLeakEnergy: UnitValue;
    tagLeakGasCost: UnitValue;
    tagLeakGasType: GasType;
    tagLeakLinePressure: UnitValue;
    tagLeakOperatingHours: number;
    tagLeakPowerRatio: UnitValue;
    tagLeakRange: string;
    tagLeakRate: UnitValue;
    tagLeakType: LeakType;
    tagLeakUnitSystem: UnitSystem;
};

export type SorXPDDData = {
    tagPDExternal: number;
    tagPDInternal: number;
    tagPDOperatingConditions: string;
    tagPDOther: number;
    tagPDTracking: number;
    pulseCount: number;
    dischargeDataPoints: Point2[];
};

export type SorXMeasurementDTO = {
    dateTime: string;
    estimatedDistance: number;
    estimatedDistanceUnit: string;
    firmwareRelease: string;
    frequencySelectionFrom: number;
    frequencySelectionFromUnit: string;
    frequencySelectionTo: number;
    frequencySelectionToUnit: string;
    ldMetadata?: {
        settings: {
            unitSystem: "metric" | "imperial";
            gasCost: number;
            gasCostUnit: string;
            gasType: GasType;
            linePressureUnit: string;
            linePressureValue: number;
            electricityCost: number;
            electricityCostUnit: string;
            powerRatio: number;
            powerRatioUnit: string;
            operatingHoursPerYear: number; // On handheld devices this was `operationHoursPerYear`, we deal with this when parsing
        };
        results: {
            unitSystem: "metric" | "imperial";
            type: LeakType;
            rate: number;
            rateUnit: string;
            electricityUsage: number;
            electricityUsageUnit: string;
            cost: number;
            costUnit: string;
            isLeakAccurate: boolean;
            reasonInaccurate: InaccurateReasonType;
        };
    };
    pdMetadata?: {
        settings: {
            operatingConditions: OperatingConditions;
        };
        results: {
            coronaPercentage: number;
            arcingPercentage: number;
            voidPercentage: number;
            trackingPercentage: number;
            nonePercentage: number;
        };
    };
    notes: string;
    sampleRate: number;
    sampleRateUnit: string;
    splAtSource: number;
    splAtSourceUnit: string;
    tags: {
        assetID: string;
        assetName: string;
        assetStatus: AssetStatusType;
        assetType: string;
    };
    targetPointDistance: number;
    targetPointDistanceUnit: string;
    type: SorXMeasurementType;
    version: number;
};

export type SorXMeasurementFullDTO = SorXMeasurementDTO & {
    image: string; // Base64 encoded image
    featureImages: string[]; // Base643 encoded image
    pulseCount: number;
    dischargeDataPoints: Point2[];
};

export function getEmptyDataForType(type: SorXMeasurementTypeEnum): ReportableMeasurement<SorXMeasurement> {
    const specialMeasurementType = type === "leakDetection" ? "SORXLD" : "SORXPDD";

    const base: Partial<SorXMeasurement> = {
        type,
        notes: "",
        dateTime: new Date(0),
        estimatedDistance: new UnitValue(0, Pressure.pascal),
        firmwareRelease: "",
        frequencySelectionFrom: new UnitValue(0, Pressure.pascal),
        frequencySelectionTo: new UnitValue(0, Pressure.pascal),
        sampleRate: new UnitValue(0, Pressure.pascal),
        splAtSource: new UnitValue(0, Pressure.pascal),
        targetPointDistance: new UnitValue(0, Pressure.pascal),
        image: "",
        featureImages: [],
        tagAssetID: 0,
        tagAssetName: "",
        tagAssetStatus: "",
        tagAssetType: "",
    };

    const generalReportInfo = {
        id: "",
        surveyName: "",
        company: "",
        name: "",
        created: new Date(0),
        notes: "",
        type: specialMeasurementType,
    };

    if (type === "leakDetection") {
        const data: SorXLDData = {
            ...base,
            tagLeakCost: new UnitValue(0, Pressure.pascal),
            tagLeakElectricityCost: new UnitValue(0, Pressure.pascal),
            tagLeakEnergy: new UnitValue(0, Pressure.pascal),
            tagLeakGasCost: new UnitValue(0, Pressure.pascal),
            tagLeakGasType: "",
            tagLeakLinePressure: new UnitValue(0, Pressure.pascal),
            tagLeakOperatingHours: 0,
            tagLeakPowerRatio: new UnitValue(0, Pressure.pascal),
            tagLeakRange: "",
            tagLeakRate: new UnitValue(0, Pressure.pascal),
            tagLeakType: "",
            tagLeakUnitSystem: UnitSystem.Metric,
        };

        return {
            ...generalReportInfo,
            data,
        } as ReportableMeasurement<SorXMeasurement>;
    } else {
        const data: SorXPDDData = {
            ...base,
            tagPDExternal: 0,
            tagPDInternal: 0,
            tagPDOperatingConditions: "",
            tagPDOther: 0,
            tagPDTracking: 0,
            pulseCount: 0,
            dischargeDataPoints: [],
        };

        return {
            ...generalReportInfo,
            data,
        } as ReportableMeasurement<SorXMeasurement>;
    }
}

export function sorXFilePathToId(filePath: string): string {
    if (filePath.startsWith("/")) {
        filePath = filePath.slice(1);
    }
    return filePath.replace(/\.sorx?$/i, "");
}

export function sorXMeasurementToReportableMeasurement(
    sorXDTO: SorXMetadataFull,
    filePath = ""
): ReportableMeasurement<SorXMeasurement> {
    let specialMeasurementType: string;
    switch (sorXDTO.type) {
        case "leakDetection":
            specialMeasurementType = "SORXLD";
            break;
        case "partialDischarge":
            specialMeasurementType = "SORXPDD";
            break;
        default:
            throw new Error(`Unknown SorXMeasurementType: ${sorXDTO.type}`);
    }

    // Extracts the measurement name from the path: the string between '.sorX' and the last '/' character.
    const measurementName = filePath.match(/([^/]+)(?=\.sorx?$)/i)?.[0];
    const filePathWithoutExtension = sorXFilePathToId(filePath);
    const generalReportInfo = {
        id: filePathWithoutExtension,
        surveyName: "",
        company: "",
        name: measurementName,
        created: new Date(sorXDTO.dateTime),
        notes: sorXDTO.notes,
        type: specialMeasurementType,
    };

    // TODO: Should be fixed on device

    // If electricity cost is in kWh, assume the unit is currency/kWh
    if (sorXDTO.ldMetadata?.settings.electricityCostUnit.toLowerCase() === "kwh") {
        sorXDTO.ldMetadata.settings.electricityCostUnit = "EUR/kWh";
    }
    // If electricity usage is in kW/year, assume the unit is kWh/year
    if (sorXDTO.ldMetadata?.results.electricityUsageUnit.toLowerCase().replace(/\s+/g, "") === "kw/year") {
        sorXDTO.ldMetadata.results.electricityUsageUnit = "kWh/year";
    }

    // Helper to get first value from array or fallback to value
    function getFirstNumber(val: number | number[] | undefined): number | undefined {
        if (Array.isArray(val)) {
            return val.length > 0 ? val[0] : undefined;
        }
        return val;
    }

    const sorXData: SorXData = {
        type: sorXDTO.type,
        notes: sorXDTO.notes,
        dateTime: new Date(sorXDTO.dateTime),
        estimatedDistance: new UnitValue(
            sorXDTO.estimatedDistance,
            parseUnitWithValidation(sorXDTO.estimatedDistanceUnit, "Estimated Distance Unit")
        ),
        firmwareRelease: sorXDTO.firmwareRelease,
        frequencySelectionFrom: new UnitValue(
            sorXDTO.frequencySelectionFrom,
            parseUnitWithValidation(sorXDTO.frequencySelectionFromUnit, "Frequency Selection (From)")
        ),
        frequencySelectionTo: new UnitValue(
            sorXDTO.frequencySelectionTo,
            parseUnitWithValidation(sorXDTO.frequencySelectionToUnit, "Frequency Selection (To)")
        ),
        sampleRate: new UnitValue(
            getFirstNumber(sorXDTO.sampleRate),
            parseUnitWithValidation(sorXDTO.sampleRateUnit, "Sample Rate")
        ),
        splAtSource: new UnitValue(
            getFirstNumber(sorXDTO.splAtSource),
            parseUnitWithValidation(sorXDTO.splAtSourceUnit, "SPL at Source")
        ),
        targetPointDistance: new UnitValue(
            sorXDTO.targetPointDistance,
            parseUnitWithValidation(sorXDTO.targetPointDistanceUnit, "Target Point Distance")
        ),
        image: sorXDTO.image,
        featureImages: sorXDTO.featureImages,

        tagAssetID: sorXDTO.tags.assetID,
        tagAssetName: sorXDTO.tags.assetName,
        tagAssetStatus: sorXDTO.tags.assetStatus,
        tagAssetType: sorXDTO.tags.assetType,
        // Optionally add sourceLocation if present
        ...(sorXDTO.sourceLocation ? { sourceLocation: sorXDTO.sourceLocation } : {}),
    };

    if (sorXDTO.type === "leakDetection") {
        return {
            ...generalReportInfo,
            data: {
                ...sorXData,
                surveyName: "",
                company: "",
                tagLeakCost: new UnitValue(
                    sorXDTO.ldMetadata.results.cost,
                    parseUnitWithValidation(sorXDTO.ldMetadata.results.costUnit, "Leak Cost", "EUR")
                ),
                tagLeakElectricityCost: new UnitValue(
                    sorXDTO.ldMetadata.settings.electricityCost,
                    parseUnitWithValidation(
                        sorXDTO.ldMetadata.settings.electricityCostUnit,
                        "Electricity Cost",
                        "EUR/kWh"
                    )
                ),
                tagLeakEnergy: new UnitValue(
                    sorXDTO.ldMetadata.results.electricityUsage,
                    parseUnitWithValidation(
                        sorXDTO.ldMetadata.results.electricityUsageUnit,
                        "Electricity Usage",
                        "kWh/year"
                    )
                ),
                tagLeakGasCost: new UnitValue(
                    sorXDTO.ldMetadata.settings.gasCost,
                    parseUnitWithValidation(sorXDTO.ldMetadata.settings.gasCostUnit, "Gas Cost", "EUR/L")
                ),
                // The gastype can be either with a space or without, so we normalize it
                tagLeakGasType:
                    GasTypesOptions.find(
                        (type) =>
                            type.toLowerCase().replace(/\s+/g, "") ===
                            sorXDTO.ldMetadata.settings.gasType.toLowerCase().replace(/\s+/g, "")
                    ) ?? "",

                tagLeakLinePressure: new UnitValue(
                    sorXDTO.ldMetadata.settings.linePressureValue,
                    parseUnitWithValidation(sorXDTO.ldMetadata.settings.linePressureUnit, "Pressure", "Pa")
                ),

                // operatingHoursPerYear is also named operationHoursPerYear in some devices, choose whichever is present
                tagLeakOperatingHours:
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (sorXDTO as any).ldMetadata?.settings?.operationHoursPerYear ??
                    sorXDTO.ldMetadata?.settings?.operatingHoursPerYear ??
                    0,
                tagLeakPowerRatio: new UnitValue(
                    sorXDTO.ldMetadata.settings.powerRatio,
                    parseUnitWithValidation(sorXDTO.ldMetadata.settings.powerRatioUnit, "Power Ratio", "kW/CFM")
                ),
                tagLeakRange: "",
                tagLeakRate: new UnitValue(
                    getFirstNumber(sorXDTO.ldMetadata.results.rate),
                    parseUnitWithValidation(sorXDTO.ldMetadata.results.rateUnit, "Leak Rate", "gal/h")
                ),
                tagLeakType: sorXDTO.ldMetadata.results.type as LeakType,
                tagLeakUnitSystem: sorXUnitSystemTypeToUnitSystemType(sorXDTO.ldMetadata.settings.unitSystem),
            },
        } as ReportableMeasurement<SorXMeasurement>;
    } else {
        return {
            ...generalReportInfo,
            data: {
                ...sorXData,
                tagPDExternal: sorXDTO.pdMetadata.results.externalPercentage ?? 0, // They are both set to _acoustics->get_pdExternal() in Cam iVex code
                tagPDInternal: sorXDTO.pdMetadata.results.internalPercentage ?? 0,
                tagPDTracking: sorXDTO.pdMetadata.results.trackingPercentage ?? 0,
                tagPDOther: sorXDTO.pdMetadata.results.nonePercentage ?? 0,
                // The operating conditions can be either with a space or without, so we normalize it
                tagPDOperatingConditions:
                    operatingConditions.find(
                        (condition) =>
                            condition.toLowerCase().replace(/\s+/g, "") ===
                            sorXDTO.pdMetadata.settings.operatingConditions.toLowerCase().replace(/\s+/g, "")
                    ) ?? 0,
                pulseCount: sorXDTO.prpdBinData?.pulseCount ?? 0,
                dischargeDataPoints: sorXDTO.prpdBinData?.dischargeDataPoints ?? [],
            },
        } as ReportableMeasurement<SorXMeasurement>;
    }

    function parseUnitWithValidation(unitString: string, variableName: string, defaultUnitString = ""): Unit {
        const unit = unitString == "" ? defaultUnitString : unitString;
        try {
            const parsedUnit = UnitParser.Parse(unit);
            if (parsedUnit == undefined) {
                throw new Error(`Could not parse unit "${unit}" of "${variableName}"`);
            }
            return parsedUnit;
        } catch {
            throw new Error(`Could not parse unit "${unit}" of "${variableName}"`);
        }
    }
}
